// client/src/dnd/views/Interactive_parts/DemoWallsGeoman.js
import React, { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

function toStandardCoords({ x, y }, imageSize) {
  return { x, y };
}

function DemoWallsGeoman({ imageSize, onDemoWallsChange }) {
  const map = useMap();
  const enabledRef = useRef(false);
  const demoLayerRef = useRef(null);
  const polysRef = useRef([]);

  useEffect(() => {
    if (!map || !map.pm) return;

    function updatePolysFromLayer(layer) {
      try {
        const geo = layer.toGeoJSON().geometry;
        if (
          geo &&
          geo.type === "Polygon" &&
          Array.isArray(geo.coordinates) &&
          Array.isArray(geo.coordinates[0])
        ) {
          const ring = geo.coordinates[0].map(([lng, lat]) =>
            toStandardCoords({ x: lng, y: lat }, imageSize)
          );
          const filtered = ring.filter(
            (p) => Number.isFinite(p.x) && Number.isFinite(p.y)
          );
          if (filtered.length > 1) {
            polysRef.current = [...polysRef.current, filtered];
            if (typeof onDemoWallsChange === "function") {
              onDemoWallsChange(polysRef.current.slice());
            }
          }
        }
      } catch {}
    }

    function ensureDemoLayer() {
      if (!demoLayerRef.current) {
        demoLayerRef.current = L.featureGroup().addTo(map);
      }
      return demoLayerRef.current;
    }

    function clearDemoWalls() {
      polysRef.current = [];
      if (typeof onDemoWallsChange === "function") {
        onDemoWallsChange([]);
      }
      if (demoLayerRef.current) {
        try {
          demoLayerRef.current.clearLayers();
        } catch {}
      }
    }

    function applyEnabled(enabled) {
      enabledRef.current = enabled;
      if (!enabled) {
        clearDemoWalls();
        try {
          if (map.pm) {
            map.pm.disableDraw();
            map.pm.removeControls();
          }
        } catch {}
        return;
      }

      ensureDemoLayer();

      try {
        if (map.pm) {
          map.pm.setGlobalOptions({
            snap: false,
            snappable: false,
            snapDistance: 0,
            snapMiddle: false,
            snapSegment: false,
            allowSelfIntersection: true,
          });

          map.pm.addControls({
            position: "topleft",
            drawCircle: false,
            drawCircleMarker: false,
            drawMarker: false,
            drawPolyline: false,
            cutPolygon: false,
            dragMode: false,
            editMode: false,
            removalMode: false,
            drawRectangle: false,
            drawPolygon: true,
            rotateMode: false,
          });
        }
      } catch {}
    }

    try {
      if (typeof window !== "undefined" && window._paDemoWallsOn) {
        applyEnabled(true);
      }
    } catch {}

    function handleToggle(ev) {
      const enabled = !!(ev && ev.detail && ev.detail.enabled);
      applyEnabled(enabled);
    }

    function handleCreate(e) {
      if (!enabledRef.current) return;
      const layer = e.layer;
      if (!layer) return;
      ensureDemoLayer().addLayer(layer);
      updatePolysFromLayer(layer);
    }

    window.addEventListener("pointatlas-demo-toggle-walls", handleToggle);
    map.on("pm:create", handleCreate);

    return () => {
      window.removeEventListener("pointatlas-demo-toggle-walls", handleToggle);
      map.off("pm:create", handleCreate);
      applyEnabled(false);
      if (demoLayerRef.current) {
        try {
          map.removeLayer(demoLayerRef.current);
        } catch {}
        demoLayerRef.current = null;
      }
    };
  }, [map, imageSize, onDemoWallsChange]);

  return null;
}

export default DemoWallsGeoman;
